import type { Slug } from '@bridebook/toolbox/src/types';
import { buildCloudflareImageUrl } from '@bridebook/toolbox/src/buildImageUrl';

// Categories that should use entropy (auto gravity)
const entropyCategories: Slug[] = [
  'jewellery',
  'venue',
  'florist',
  'cakes',
  'stationery',
  'transport',
  'decoration',
  'marquee',
];

/**
 * Legacy Imgix parameters interface for backward compatibility
 */
interface ImgixParams {
  src: string;
  crop?: string;
  fit?: string;
  slug?: Slug;
  w?: number;
  h?: number;
  customParams?: object;
  forceCleanSrc?: boolean;
  quality?: number;
}

/**
 * Maps Imgix crop values to Cloudflare Images gravity values
 */
const mapCropToGravity = (crop: string): string => {
  switch (crop) {
    case 'entropy':
      return 'auto';
    case 'faces':
      return 'face';
    case 'center':
      return 'center';
    default:
      return crop;
  }
};
/**
 * Maps Imgix fit values to Cloudflare Images fit values
 */
const mapFitToCloudflare = (imgixFit: string): 'cover' | 'contain' | 'crop' | 'pad' => {
  switch (imgixFit) {
    case 'crop':
      return 'cover';
    case 'scale':
      return 'contain';
    case 'clip':
      return 'crop';
    case 'pad':
    case 'fill':
      return 'pad';
    default:
      return 'cover';
  }
};
/**
 * For ImageX component: Handles Imgix legacy parameters and category logic
 */
export const translateImgixToCloudflare = (params: ImgixParams): string => {
  const { src, crop, fit = 'crop', slug = 'photo', w, h, forceCleanSrc = false, quality } = params;

  // Determine gravity based on crop parameter and category
  let gravity: string;
  if (crop) {
    gravity = mapCropToGravity(crop);
  } else {
    // Use category-based logic when no crop is specified
    const isEntropyCategory = entropyCategories.includes(slug);
    gravity = isEntropyCategory ? 'auto' : 'face';
  }

  // Map fit value
  const cloudflareFit = mapFitToCloudflare(fit);

  // Use the core function to build the URL
  return buildCloudflareImageUrl({
    src,
    w,
    h,
    fit: cloudflareFit,
    gravity,
    forceCleanSrc,
    quality,
  });
};
